'use client';

import Image from 'next/image';
import { useEffect, useId, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { marqueeCredentials } from '@/data/credentialsData';

/**
 * Credentials row — second version of CredentialsMarquee. Tiles stretch to fill the row;
 * when more credentials are added than fit, the row scrolls horizontally with arrow
 * buttons. Certificates with a PDF open in a new tab.
 *
 * @param {Array}   [props.items]   Array of { href, src, alt }
 * @param {string}  [props.label]   Text shown before the logos
 * @param {boolean} [props.isDark]  Use the light-on-dark colour scheme
 */
export default function CredentialsStrip({
  items = marqueeCredentials,
  label = 'Certified & Recognised By',
  isDark = true,
}) {
  const listRef = useRef(null);
  const labelId = useId();
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const update = () => {
      setCanScroll({
        left: list.scrollLeft > 4,
        right: list.scrollLeft + list.clientWidth < list.scrollWidth - 4,
      });
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(list);
    return () => {
      list.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [items.length]);

  const scrollBy = (dir) => {
    const list = listRef.current;
    if (list) list.scrollBy({ left: dir * list.clientWidth * 0.8, behavior: 'smooth' });
  };

  const arrowClass = `absolute top-1/2 -translate-y-1/2 z-10 size-8 rounded-full flex items-center justify-center shadow-md transition-opacity duration-200 ${isDark ? 'bg-white text-slate-900 hover:bg-red-500 hover:text-white' : 'bg-slate-900 text-white hover:bg-red-500'
    }`;

  return (
    <div
      className={`relative border-t backdrop-blur-md ${isDark ? 'border-white/10 bg-white/[0.04]' : 'border-slate-200 bg-white'
        }`}
    >
      {isDark && (
        <span className="absolute inset-x-0 -top-px h-px bg-linear-to-r from-transparent via-red-500/50 to-transparent" aria-hidden="true" />
      )}
      <div className="max-w-[1440px] mx-auto px-6 py-6 flex flex-col lg:flex-row items-center gap-4 lg:gap-8">
        <p id={labelId} className={`shrink-0 text-center text-xs sm:text-sm font-semibold tracking-widest uppercase ${isDark ? 'text-blue-100/80' : 'text-slate-600'}`}>
          {label}
        </p>

        <span className={`hidden lg:block h-10 w-px ${isDark ? 'bg-white/15' : 'bg-slate-200'}`} aria-hidden="true" />

        <div className="relative w-full min-w-0">
          <ul
            ref={listRef}
            aria-labelledby={labelId}
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {items.map((item) => {
              const tile = (
                <>
                  <Image
                    src={item.src}
                    alt={item.alt}
                    fill
                    sizes="200px"
                    draggable="false"
                    className="object-contain p-2.5"
                  />
                  {item.href && (
                    <span className="absolute top-1.5 right-1.5 size-5 rounded-full bg-slate-900/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <ExternalLink size={11} />
                    </span>
                  )}
                </>
              );
              const tileClass = 'group relative block h-16 rounded-xl bg-white border border-white/20 shadow-sm transition-all duration-200';

              return (
                <li key={item.alt} className="flex-[1_0_150px] sm:flex-[1_0_180px] snap-start">
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${item.alt} — view certificate (opens in a new tab)`}
                      className={`${tileClass} hover:-translate-y-0.5 hover:shadow-lg hover:shadow-red-900/20 hover:border-red-400/60`}
                    >
                      {tile}
                    </a>
                  ) : (
                    <div className={tileClass}>{tile}</div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* disabled (not just transparent) so hidden arrows drop out of the tab order */}
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!canScroll.left}
            aria-label="Scroll credentials left"
            className={`${arrowClass} -left-3 ${canScroll.left ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!canScroll.right}
            aria-label="Scroll credentials right"
            className={`${arrowClass} -right-3 ${canScroll.right ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
